/**
 * Page through a list endpoint instead of asking for the whole result set at once.
 *
 * `_limit=-1` builds the entire filtered set in one response -- 27.8 MB for 500 statement rows
 * before the column projection, and one measured request grew an api pod's RSS by 1919 MB -- on a
 * single-threaded node process. Fetching the same rows a page at a time bounds the response in
 * flight to `concurrency * pageSize` rows.
 *
 * Merges `data` only. An endpoint that also returns aggregates (`sums`, ...) needs its own reduce;
 * see the ARAP list export in `components/accounting/arapList.tsx`.
 *
 * Deliberately imports nothing: components under a wholesale `jest.mock('@/utils', ...)` still get
 * the real module through this subpath.
 *
 * https://github.com/smartforwarder/app/issues/6302
 */

/** The `{ data, total, success }` envelope every `AntdProRequest` service resolves with. */
export type PagedResult<T> = {
  data?: T[];
  total?: number;
  success?: boolean;
} & Record<string, any>;

export type FetchPage<T> = (page: { pageSize: number; current: number }) => Promise<PagedResult<T>>;

/** Excel exports page at 100 rows. 500 is the page size behind the 1.9 GB request in #6263. */
export const EXPORT_PAGE_SIZE = 100;
/** Two requests in flight: the api pods are single threaded, more only queues behind them. */
export const EXPORT_CONCURRENCY = 2;

export async function fetchAllPages<T>(
  fetchPage: FetchPage<T>,
  {
    pageSize = EXPORT_PAGE_SIZE,
    concurrency = EXPORT_CONCURRENCY,
  }: { pageSize?: number; concurrency?: number } = {},
): Promise<PagedResult<T>> {
  // Count probe: one row, so `total` comes back without paying for a page of data.
  const countResult = await fetchPage({ pageSize: 1, current: 1 });
  if (!countResult?.total) {
    // 0 rows: hand back the probe unchanged. Its `data` is `[]`, so callers still write the empty
    // workbook they wrote before this change.
    return countResult;
  }

  const totalPages = Math.ceil(countResult.total / pageSize);
  const results: PagedResult<T>[] = [];
  for (let i = 0; i < totalPages; i += concurrency) {
    const batch = Array.from({ length: Math.min(concurrency, totalPages - i) }, (_, index) =>
      fetchPage({ pageSize, current: i + index + 1 }),
    );
    // the await is assigned before spreading: babel's regenerator transform rejects a spread of an
    // awaited expression inside a call argument
    // eslint-disable-next-line no-await-in-loop
    const settled = await Promise.all(batch);
    results.push(...settled);
  }

  return results.reduce<PagedResult<T>>(
    (acc, result, index) => {
      // A failed page resolves with the error body rather than rejecting (`errorHandler` in
      // utils/request.js returns instead of rethrowing, and umi-request resolves with that).
      // Skipping it would download a workbook silently one page short, which for accounting data
      // is worse than no workbook at all.
      if (!Array.isArray(result?.data)) {
        throw new Error(`fetchAllPages: page ${index + 1} of ${totalPages} returned no data`);
      }
      return { ...acc, data: [...(acc.data || []), ...result.data] };
    },
    { data: [], total: countResult.total, success: true },
  );
}
